import { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/ScrollReveal";

const INITIAL_STATE = {
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
};

export default function Contact() {
    const [form, setForm] = useState(INITIAL_STATE);
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({ ...f, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                phone: form.phone.trim() || null,
            };
            await api.post("/contact", payload);
            toast.success("Message envoyé ! Nous revenons vers vous très vite.");
            setSent(true);
            setForm(INITIAL_STATE);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Impossible d'envoyer le message. Merci de réessayer dans un instant."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="page-contact" className="bg-white">
            {/* Hero */}
            <section className="relative bg-brand-ink text-white pt-40 pb-24 md:pt-48 md:pb-32 grain">
                <div className="mx-auto max-w-7xl px-6">
                    <Reveal>
                        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-brand-orange">
                            Contact
                        </span>
                        <h1 className="mt-6 max-w-4xl font-display text-5xl font-normal leading-[1.03] tracking-tightest sm:text-6xl lg:text-7xl">
                            Parlons de votre <em className="italic text-brand-orange">projet de formation</em>.
                        </h1>
                        <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
                            Une question sur un cursus, une demande d'inscription groupée, un besoin particulier ?
                            Notre équipe vous répond sous 48h ouvrées.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* Form + Infos */}
            <section className="py-24 md:py-32 bg-white">
                <div className="mx-auto max-w-7xl px-6 grid gap-14 lg:grid-cols-12">
                    {/* Form */}
                    <Reveal className="lg:col-span-7">
                        {sent ? (
                            <div className="rounded-3xl border border-brand-line bg-brand-sand p-10 text-center" data-testid="contact-success">
                                <CheckCircle2 className="mx-auto h-10 w-10 text-brand-orange" />
                                <h2 className="mt-6 font-display text-3xl">Merci pour votre message.</h2>
                                <p className="mt-3 text-brand-ink/75">
                                    Nous revenons vers vous très prochainement à l'adresse indiquée.
                                </p>
                                <button
                                    type="button"
                                    data-testid="contact-new-message"
                                    onClick={() => setSent(false)}
                                    className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-semibold text-white hover:bg-brand-coal"
                                >
                                    Envoyer un nouveau message
                                </button>
                            </div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                data-testid="contact-form"
                                className="rounded-3xl border border-brand-line bg-white p-8 md:p-10 shadow-premium"
                            >
                                <h2 className="font-display text-3xl">Formulaire de contact</h2>
                                <p className="mt-2 text-sm text-brand-smoke">
                                    Tous les champs marqués d'un * sont obligatoires.
                                </p>

                                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div className="sm:col-span-1">
                                        <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                            Nom complet *
                                        </label>
                                        <input
                                            id="name"
                                            name="name"
                                            required
                                            value={form.name}
                                            onChange={handleChange}
                                            data-testid="contact-input-name"
                                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                            Email *
                                        </label>
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={handleChange}
                                            data-testid="contact-input-email"
                                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                            Téléphone
                                        </label>
                                        <input
                                            id="phone"
                                            name="phone"
                                            value={form.phone}
                                            onChange={handleChange}
                                            data-testid="contact-input-phone"
                                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <label htmlFor="subject" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                            Sujet *
                                        </label>
                                        <input
                                            id="subject"
                                            name="subject"
                                            required
                                            value={form.subject}
                                            onChange={handleChange}
                                            data-testid="contact-input-subject"
                                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                        />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-brand-ink">
                                            Votre message *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            required
                                            rows={6}
                                            value={form.message}
                                            onChange={handleChange}
                                            minLength={10}
                                            data-testid="contact-input-message"
                                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-base outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="contact-submit"
                                    className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-60"
                                >
                                    {submitting ? "Envoi en cours…" : (<>Envoyer mon message <Send className="h-4 w-4" /></>)}
                                </button>
                            </form>
                        )}
                    </Reveal>

                    {/* Infos */}
                    <Reveal className="lg:col-span-5" delay={0.15}>
                        <div className="rounded-3xl bg-brand-sand p-8 md:p-10 border border-brand-line">
                            <h3 className="font-display text-2xl">Informations</h3>
                            <p className="mt-3 text-sm text-brand-ink/70">
                                Notre équipe est à votre disposition du lundi au vendredi, 9h–18h.
                            </p>

                            <div className="mt-8 space-y-5 text-sm">
                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <Mail className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-brand-smoke">Email</div>
                                        <a href="mailto:contact@cursus-physio.fr" className="mt-1 block font-medium text-brand-ink hover:text-brand-orange">
                                            contact@cursus-physio.fr
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <Phone className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-brand-smoke">Téléphone</div>
                                        <a href="tel:+33186760000" className="mt-1 block font-medium text-brand-ink hover:text-brand-orange">
                                            +33 1 86 76 00 00
                                        </a>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange">
                                        <MapPin className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-brand-smoke">Adresse</div>
                                        <div className="mt-1 font-medium text-brand-ink">
                                            12 rue du Faubourg Saint-Antoine
                                            <br /> 75011 Paris
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 overflow-hidden rounded-3xl border border-brand-line shadow-premium">
                            <img
                                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=1280&q=80"
                                alt="Paris — carte"
                                className="h-64 w-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
